import {
  ContentType,
  MessageExtractKind,
  MessageExtractOutcome,
  MessageExtractStatus,
  TemplateLayout,
} from './enums';
import { z } from 'zod';
import { browsableContentTypesSchema } from './content-browsable';
import { contentFilterSectionsSchema, contentListProjectionSchema, obligationFilterSchema } from './filter';
import { mutableContentTypeSchema, messageExtractHighlightSchema } from './content-entities';

// ============================================================================
// Zod Schemas for Input Validation
// ============================================================================

// Base schemas
const contentCreateBaseSchema = z.object({
  contentType: mutableContentTypeSchema,
  title: z.string().optional(),
  description: z.string().optional(),
  memberId: z.number(),
  /** Provenance apex (e.g. the NeoChat a proposal card came from) that owns this creation — suppresses ManualSession adoption and becomes the created content's provenance apex. */
  originProvenanceContentId: z.coerce.number().int().positive().optional()
});

const contentUpdateBaseSchema = z.object({
  contentType: mutableContentTypeSchema,
  title: z.string().optional(),
  description: z.string().optional()
});

// Type-specific create schemas
const anchorCreateSchema = contentCreateBaseSchema.extend({
  contentType: z.literal(ContentType.ANCHOR)
});

const eventCreateSchema = contentCreateBaseSchema.extend({
  contentType: z.literal(ContentType.EVENT),
  startDate: z.string().datetime().optional(),
  endDate: z.string().datetime().optional(),
  parentAnchorId: z.number()
});

// Multipart form fields arrive as strings, so a JSON-encoded object is accepted too.
const observationGroupSchema = z.preprocess(
  (value) => (typeof value === 'string' ? JSON.parse(value) : value),
  z.union([
    z.object({ groupId: z.coerce.number().int().positive() }),
    z.object({ start: z.literal(true) }),
  ]),
);

const photoCreateSchema = contentCreateBaseSchema.extend({
  contentType: z.literal(ContentType.PHOTO),
  linkToContentId: z.coerce.number().int().positive().optional(),
  uploadPurpose: z.string().trim().min(1).optional(),
  observationGroup: observationGroupSchema.optional(),
  // File is added by multer middleware
  file: z.any()
});

const recordingCreateSchema = contentCreateBaseSchema.extend({
  contentType: z.literal(ContentType.RECORDING),
  length: z.number().optional(),
  linkToContentId: z.coerce.number().int().positive().optional(),
  uploadPurpose: z.string().trim().min(1).optional(),
  // For text-based recordings (alternative to file upload)
  text: z.string().optional(),
  // Allow transcript to be set manually
  transcript: z.string().optional(),
  observationGroup: observationGroupSchema.optional(),
  // File is added by multer middleware
  file: z.any().optional()
});

const videoCreateSchema = contentCreateBaseSchema.extend({
  contentType: z.literal(ContentType.VIDEO),
  linkToContentId: z.coerce.number().int().positive().optional(),
  uploadPurpose: z.string().trim().min(1).optional(),
  observationGroup: observationGroupSchema.optional(),
  // Multipart fields arrive as strings; the client supplies the duration.
  durationSeconds: z.coerce.number().int().nonnegative().optional(),
  // File is added by multer middleware
  file: z.any()
});

const documentCreateSchema = contentCreateBaseSchema.extend({
  contentType: z.literal(ContentType.DOCUMENT),
  parentCode: z.string().nullable().optional(),
  sortOrder: z.number().int().optional()
}).strict();

const fileCreateSchema = contentCreateBaseSchema.extend({
  contentType: z.literal(ContentType.FILE),
  parentContentId: z.number().optional(),
  uploadPurpose: z.string().trim().min(1).optional(),
  /** Internal dedupe / trace key (e.g. Gmail import); not shown to members when a human description is set. */
  importSourceKey: z.string().trim().min(1).optional(),
  // File is added by multer middleware
  file: z.any()
});

const urlCreateSchema = contentCreateBaseSchema.extend({
  contentType: z.literal(ContentType.URL),
  url: z.string().url(),
  parentContentId: z.number().optional()
}).strict();

const messageExtractCreateSchema = contentCreateBaseSchema.extend({
  contentType: z.literal(ContentType.MESSAGE_EXTRACT),
  sourceContentId: z.number().int().positive(),
  kind: z.nativeEnum(MessageExtractKind),
  dueAt: z.string().datetime().optional(),
  assigneePartyId: z.number().int().positive().optional(),
  highlight: messageExtractHighlightSchema,
  burstId: z.number().int().positive().optional()
}).strict();

const templateCreateSchema = contentCreateBaseSchema.extend({
  contentType: z.literal(ContentType.TEMPLATE),
  title: z.string().min(1),
  whenToUse: z.string(),
  layout: z.nativeEnum(TemplateLayout).optional(),
  parentContentId: z.number().optional()
}).strict();

export const contentCreateSchema = z.discriminatedUnion('contentType', [
  anchorCreateSchema,
  eventCreateSchema,
  photoCreateSchema,
  recordingCreateSchema,
  videoCreateSchema,
  documentCreateSchema,
  fileCreateSchema,
  urlCreateSchema,
  messageExtractCreateSchema,
  templateCreateSchema
])
  // Recording validation: Must have either file or text
  .refine(
    (data) => data.contentType !== ContentType.RECORDING || !!(data.file || data.text),
    {
      message: 'Recording must include either an audio file or text input.',
      path: ['file', 'text']
    }
  )
;

// Type-specific update schemas
const anchorUpdateSchema = contentUpdateBaseSchema.extend({
  contentType: z.literal(ContentType.ANCHOR)
});

const eventUpdateSchema = contentUpdateBaseSchema.extend({
  contentType: z.literal(ContentType.EVENT),
  startDate: z.string().optional(),
  endDate: z.string().optional()
});

const photoUpdateSchema = contentUpdateBaseSchema.extend({
  contentType: z.literal(ContentType.PHOTO),
});

const recordingUpdateSchema = contentUpdateBaseSchema.extend({
  contentType: z.literal(ContentType.RECORDING),
});

const videoUpdateSchema = contentUpdateBaseSchema.extend({
  contentType: z.literal(ContentType.VIDEO),
});

const documentUpdateSchema = contentUpdateBaseSchema.extend({
  contentType: z.literal(ContentType.DOCUMENT),
  parentCode: z.string().nullable().optional(),
  sortOrder: z.number().int().optional()
}).strict();

export const fileReferenceUpdateSchema = z.object({
  providerEtag: z.string().nullable(),
  providerPath: z.string().nullable(),
  providerModifiedAt: z.string().datetime().nullable(),
  webUrl: z.string().nullable(),
  mimeType: z.string().nullable(),
  fetchMimeType: z.string().nullable(),
  size: z.number().int().nullable(),
}).strict();

const fileUpdateSchema = contentUpdateBaseSchema.extend({
  contentType: z.literal(ContentType.FILE),
  file: fileReferenceUpdateSchema.optional(),
}).strict();

const urlUpdateSchema = contentUpdateBaseSchema.extend({
  contentType: z.literal(ContentType.URL),
  url: z.string().url().optional()
}).strict();

const neoChatUpdateSchema = contentUpdateBaseSchema.extend({
  contentType: z.literal(ContentType.NEO_CHAT),
});

const messageExtractUpdateSchema = contentUpdateBaseSchema.extend({
  contentType: z.literal(ContentType.MESSAGE_EXTRACT),
  status: z.nativeEnum(MessageExtractStatus).optional(),
  outcome: z.nativeEnum(MessageExtractOutcome).nullable().optional(),
  dueAt: z.string().datetime().nullable().optional(),
  kind: z.nativeEnum(MessageExtractKind).optional(),
}).strict();

const templateUpdateSchema = contentUpdateBaseSchema.extend({
  contentType: z.literal(ContentType.TEMPLATE),
  whenToUse: z.string().optional(),
  layout: z.nativeEnum(TemplateLayout).optional(),
}).strict();

export const contentUpdateSchema = z.discriminatedUnion('contentType', [
  anchorUpdateSchema,
  eventUpdateSchema,
  photoUpdateSchema,
  recordingUpdateSchema,
  videoUpdateSchema,
  documentUpdateSchema,
  fileUpdateSchema,
  urlUpdateSchema,
  neoChatUpdateSchema,
  messageExtractUpdateSchema,
  templateUpdateSchema,
]);

export const messageExtractUpdateLinkSchema = z.object({
  sourceMessageContentId: z.number().int().positive(),
  highlight: messageExtractHighlightSchema,
}).strict();

export const messageExtractReassignParamsSchema = z.object({
  assigneePartyId: z.number().int().positive(),
  updateLink: messageExtractUpdateLinkSchema.optional(),
  delegated: z.boolean().optional(),
}).strict();

export const messageExtractApplyUpdateFromMessageParamsSchema = z.object({
  sourceMessageContentId: z.number().int().positive(),
  status: z.nativeEnum(MessageExtractStatus).optional(),
  outcome: z.nativeEnum(MessageExtractOutcome).optional(),
  assigneePartyId: z.number().int().positive().optional(),
  delegated: z.boolean().optional(),
  highlight: messageExtractHighlightSchema,
  note: z.string().trim().max(500).optional(),
}).strict();

export type MessageExtractUpdateLink = z.infer<typeof messageExtractUpdateLinkSchema>;
export type MessageExtractReassignParams = z.infer<typeof messageExtractReassignParamsSchema>;
export type MessageExtractApplyUpdateFromMessageParams = z.infer<
  typeof messageExtractApplyUpdateFromMessageParamsSchema
>;

export type MessageExtractionActioneeRule =
  | 'named in text'
  | 'single To'
  | 'first of multiple To'
  | 'NONE (no resolvable recipient)'
  | 'UNRESOLVED (party lookup failed)';

export interface MessageExtractionReasoningItem {
  extractId: number;
  kind: MessageExtractKind;
  quote: string;
  rationale: string;
  dueAtText: string | null;
  actionee: string | null;
  actioneeRule: MessageExtractionActioneeRule;
}

export interface MessageExtractionSkippedItem {
  kind: MessageExtractKind;
  quote: string;
  rationale: string;
  reason: string;
}

export interface MessageExtractionAppliedTransition {
  extractId: number;
  eventType: string;
}

export interface MessageExtractionSkippedTransition {
  extractId: number;
  eventType: string;
  reason: string;
}

export interface MessageExtractionRunResult {
  createdExtractIds: number[];
  reasoning: MessageExtractionReasoningItem[];
  skipped: MessageExtractionSkippedItem[];
  appliedTransitions: MessageExtractionAppliedTransition[];
  skippedTransitions: MessageExtractionSkippedTransition[];
}

// ============================================================================
// Inferred TypeScript Types from Zod Schemas
// ============================================================================

// Create Types
export type ContentCreateBase = z.infer<typeof contentCreateBaseSchema>;
export type AnchorCreate = z.infer<typeof anchorCreateSchema>;
export type EventCreate = z.infer<typeof eventCreateSchema>;
export type PhotoCreate = z.infer<typeof photoCreateSchema>;
export type RecordingCreate = z.infer<typeof recordingCreateSchema>;
export type VideoCreate = z.infer<typeof videoCreateSchema>;
export type DocumentCreate = z.infer<typeof documentCreateSchema>;
export type FileCreate = z.infer<typeof fileCreateSchema>;
export type UrlCreate = z.infer<typeof urlCreateSchema>;
export type MessageExtractCreate = z.infer<typeof messageExtractCreateSchema>;
export type TemplateCreate = z.infer<typeof templateCreateSchema>;
export type ContentCreate = z.infer<typeof contentCreateSchema>;

// Update Types
export type ContentUpdateBase = z.infer<typeof contentUpdateBaseSchema>;
export type AnchorUpdate = z.infer<typeof anchorUpdateSchema>;
export type EventUpdate = z.infer<typeof eventUpdateSchema>;
export type PhotoUpdate = z.infer<typeof photoUpdateSchema>;
export type RecordingUpdate = z.infer<typeof recordingUpdateSchema>;
export type VideoUpdate = z.infer<typeof videoUpdateSchema>;
export type DocumentUpdate = z.infer<typeof documentUpdateSchema>;
export type FileUpdate = z.infer<typeof fileUpdateSchema>;
export type UrlUpdate = z.infer<typeof urlUpdateSchema>;
export type NeoChatUpdate = z.infer<typeof neoChatUpdateSchema>;
export type MessageExtractUpdate = z.infer<typeof messageExtractUpdateSchema>;
export type TemplateUpdate = z.infer<typeof templateUpdateSchema>;
export type ContentUpdate = z.infer<typeof contentUpdateSchema>;



// Content list query schema
export const contentListQuerySchema = z.object({
  projection: contentListProjectionSchema.default('CONTENT'),
  contentTypes: browsableContentTypesSchema.optional(),
  /** When true, treat missing/empty contentTypes as explicit empty (yield no results). Used for invalid tab tokens. */
  contentTypesExplicitEmpty: z.coerce.boolean().optional(),
  sections: contentFilterSectionsSchema.optional(),
  obligations: obligationFilterSchema.optional(),
  parentAnchorId: z.coerce.number().int().positive().optional(),
  linkedToContentId: z.coerce.number().int().positive().optional(),
  /** Instances of one TEMPLATE: head DOCUMENT roots holding a REFERENCES link to it (any revision). */
  templateId: z.coerce.number().int().positive().optional(),
  createdByMemberIds: z.array(z.coerce.number()).optional(),
  tagIds: z.array(z.coerce.number()).optional(),
  search: z.string().optional(),
  dateFrom: z.string().datetime().optional(),
  dateTo: z.string().datetime().optional(),
  isRead: z.coerce.boolean().optional(),
  isStarred: z.coerce.boolean().optional(),
  isFollowing: z.coerce.boolean().optional(),
  /** Restrict to content a capture (PHOTO/RECORDING) may bind to — see CAPTURE_LINK_TARGET_CONTENT_TYPES. */
  captureLinkTargetsOnly: z.coerce.boolean().optional(),
  orderBy: z.enum(['timelineAt', 'createdAt', 'updatedAt', 'title', 'importance', 'relevance']).optional(),
  orderDir: z.enum(['asc', 'desc']).optional(),
  cursor: z.coerce.number().optional(),
  limit: z.coerce.number().optional(),
});

export type ContentListQuery = z.infer<typeof contentListQuerySchema>;
export type ContentListQueryInput = z.input<typeof contentListQuerySchema>;